/* Короткие уведомления внизу экрана (успех, ошибка, информация, с действием «Отменить»). */
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { errorText } from "../lib/api";
import { Icon } from "./Icon";

type Kind = "ok" | "bad" | "info";

interface ToastItem {
  id: number;
  text: ReactNode;
  kind: Kind;
  action?: { label: string; fn: () => void };
}

interface ToastApi {
  (text: ReactNode, kind?: Kind, action?: { label: string; fn: () => void }): void;
  error: (e: unknown) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast вне ToastProvider");
  return v;
}

let seq = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const remove = useCallback((id: number) => setItems((s) => s.filter((x) => x.id !== id)), []);

  const show = useCallback(
    (text: ReactNode, kind: Kind = "ok", action?: ToastItem["action"]) => {
      const id = seq++;
      setItems((s) => [...s.slice(-3), { id, text, kind, action }]);
      setTimeout(() => remove(id), action ? 6000 : 3600);
    },
    [remove],
  ) as ToastApi;
  show.error = (e: unknown) => show(errorText(e), "bad");

  return (
    <Ctx.Provider value={show}>
      {children}
      {createPortal(
        <div className="toasts" role="status" aria-live="polite">
          {items.map((t) => (
            <div key={t.id} className={"toast toast-" + t.kind}>
              <Icon name={t.kind === "bad" ? "alert" : t.kind === "info" ? "info" : "check"} />
              <span>{t.text}</span>
              {t.action ? (
                <button
                  className="btn"
                  onClick={() => {
                    t.action!.fn();
                    remove(t.id);
                  }}
                >
                  {t.action.label}
                </button>
              ) : null}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}
